import { AircraftState } from '../sim/AircraftState';
import { sampleISA } from '../sim/Atmosphere';
import { RHO0, MAX_RPM } from '../sim/constants';

const MS_TO_KTS = 1.94384;
const M_TO_FT = 3.28084;

export interface InstrumentReadings {
  ias: number; // knots indicated
  tas: number; // knots true
  altitude: number; // ft MSL
  altimeterSetting: number; // inHg (Kollsman)
  vsi: number; // ft/min (lagged)
  headingDeg: number; // magnetic
  pitchDeg: number;
  rollDeg: number;
  turnRate: number; // deg/s (right positive)
  slip: number; // -1 (ball right) .. +1 (ball left)
  rpm: number;
  rpmFraction: number;
  oatC: number;
  fuelL: number;
  fuelFraction: number;
  onGround: boolean;
}

/**
 * Converts raw physical state into the indicated values a pilot reads, including
 * a little sensor realism (IAS from density, lagged VSI, magnetic variation).
 */
export class Instruments {
  private vsiLag = 0;
  altimeterSetting = 29.92;
  magneticVariation = 0; // deg (east positive); 0 for the training field

  update(state: AircraftState, dt: number): InstrumentReadings {
    const atmo = sampleISA(state.altitude);
    const densityRatio = atmo.density / RHO0;
    const ias = state.tas * Math.sqrt(densityRatio) * MS_TO_KTS;

    // VSI with a ~1 s first-order lag, like a real instrument.
    const targetVsi = state.verticalSpeed * M_TO_FT * 60;
    this.vsiLag += (targetVsi - this.vsiLag) * Math.min(1, dt * 1.1);

    // Heading rate (turn coordinator) from body rates.
    const sphi = Math.sin(state.phi), cphi = Math.cos(state.phi);
    const cth = Math.cos(state.theta) || 1e-3;
    const psidot = (sphi * state.q + cphi * state.r) / cth;
    const turnRate = (psidot * 180) / Math.PI;

    // Slip/skid: sideslip drives the inclinometer ball.
    const slip = clamp(-state.beta * 4, -1, 1);

    const headingMag = wrap360(
      (state.psi * 180) / Math.PI - this.magneticVariation,
    );

    return {
      ias: Math.max(0, ias),
      tas: state.tas * MS_TO_KTS,
      altitude: state.altitude * M_TO_FT,
      altimeterSetting: this.altimeterSetting,
      vsi: this.vsiLag,
      headingDeg: headingMag,
      pitchDeg: (state.theta * 180) / Math.PI,
      rollDeg: (state.phi * 180) / Math.PI,
      turnRate,
      slip,
      rpm: state.rpm,
      rpmFraction: clamp(state.rpm / MAX_RPM, 0, 1),
      oatC: atmo.temperature - 273.15,
      fuelL: state.fuel,
      fuelFraction: clamp(state.fuel / 200, 0, 1),
      onGround: state.onGround,
    };
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
function wrap360(d: number): number {
  d %= 360;
  return d < 0 ? d + 360 : d;
}
